import _jsx from "@babel/runtime/helpers/jsx";
var _Glyphicon;
import React, { useCallback } from 'react';
import { deprecate } from 'react-is-deprecated';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { sendClick } from 'reaxl-analytics';
import { ownerCall } from 'reaxl-analytics-handlers';
import { Button, Glyphicon, IconButton } from 'reaxl';
import { useFeatures } from '@atc/features';
import { createChainedFunction } from '@atc/create-chained-function';
import PhoneNumberRevealAction from '../PhoneNumberRevealAction/index.js';

/**
 *  The OwnerPhoneNumber component will accept text and a phone number.
 *  If privateNumber, the phone number will be hidden.
 *
 */
function OwnerPhoneNumber(_ref) {
  let {
    analyticsData = {},
    buttonLabel = 'Call Dealer',
    buttonStyle = 'secondary',
    buttonSize,
    iconClassName,
    className,
    fireTelmetricsTag = () => {},
    // deprecated
    isCallable,
    phone = {},
    phoneClickHandler = () => {},
    uiContext = 'default',
    showIcon = false,
    isSnapshot = false,
    ...rest
  } = _ref;
  // deprecate fireTelmetricsTag
  phoneClickHandler = createChainedFunction(phoneClickHandler, fireTelmetricsTag);
  const {
    call: [isCallEnabled]
  } = useFeatures(['call']);
  const {
    privateNumber = false,
    linkText = "Get this seller's phone number",
    value = '',
    extension
  } = phone;

  // remove .isVisible once monolith is retired and move to above destructuring
  const isVisible = phone.isVisible || phone.visible;
  const handleCall = useCallback(event => {
    event.stopPropagation();
    sendClick(ownerCall, event, {
      ...analyticsData
    });
    phoneClickHandler(value);
  }, [analyticsData, phoneClickHandler, value]);
  const handleReveal = useCallback(event => {
    event.stopPropagation();
    sendClick('ownerPhoneNumber', event, analyticsData);
  }, [analyticsData]);
  if (!isCallEnabled) {
    return null;
  }
  if (['iconbutton', 'iconbutton-horizontal'].includes(uiContext) && !!value) {
    return /*#__PURE__*/_jsx(IconButton, {
      "data-cmp": "ownerPhoneCTA",
      glyph: "phone",
      className: className,
      label: buttonLabel,
      layout: uiContext === 'iconbutton' ? 'stacked' : 'horizontal',
      onClick: handleCall,
      href: `tel:${value}`,
      rel: "nofollow noopener",
      size: uiContext === 'iconbutton' ? 'large' : 'medium',
      target: "_blank"
    });
  }
  if (uiContext === 'button' && !!value) {
    return /*#__PURE__*/React.createElement(Button, Object.assign({
      "data-cmp": "ownerPhoneCTA",
      className: className,
      href: `tel:${value}`,
      rel: "nofollow",
      bsStyle: buttonStyle,
      bsSize: buttonSize,
      onClick: handleCall,
      "aria-label": "Phone"
    }, rest), /*#__PURE__*/_jsx(Glyphicon, {
      glyph: "phone",
      className: clsx({
        'margin-right-2': buttonStyle !== 'link'
      }, iconClassName),
      "aria-hidden": "true"
    }), buttonLabel);
  }
  if (uiContext === 'glyph-button' && !!value) {
    return /*#__PURE__*/React.createElement(Button, Object.assign({
      "data-cmp": "ownerPhoneCTA",
      className: className,
      href: `tel:${value}`,
      rel: "nofollow",
      bsStyle: buttonStyle,
      bsSize: buttonSize,
      onClick: handleCall,
      "aria-label": buttonLabel || 'Phone'
    }, rest), _Glyphicon || (_Glyphicon = /*#__PURE__*/_jsx(Glyphicon, {
      glyph: "phone",
      "aria-hidden": "true",
      className: "text-size-500"
    })));
  }
  if (privateNumber) {
    return /*#__PURE__*/React.createElement("span", Object.assign({
      className: className
    }, rest), linkText);
  }
  return !!value && /*#__PURE__*/React.createElement(PhoneNumberRevealAction, Object.assign({
    className: className,
    phoneNumber: value,
    extension: extension,
    linkText: linkText,
    isVisible: isVisible,
    onReveal: handleReveal,
    isCallable: isCallable,
    onCall: handleCall,
    key: value,
    showIcon: showIcon,
    isSnapshot: isSnapshot
  }, rest));
}
export default OwnerPhoneNumber;